import { Mail, Phone, Star, Briefcase, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';

interface ContractorInfo {
  id: string;
  user_first_name: string;
  user_email: string;
  user_phone: string;
  profile_image?: string;
  sent_at?: string;
  accepted_at?: string;
}

interface ContractorCardProps {
  contractor: any;
  onMessage: (contractor: ContractorInfo) => void;
  type: 'requested' | 'accepted';
}

export function ContractorCard({ contractor, onMessage, type }: ContractorCardProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all">

      {/* HEADER */}
      <div className="flex items-center gap-4">
        {/* Avatar */}
        {contractor.profile_image ? (
          <img
            src={contractor.profile_image}
            className="w-14 h-14 rounded-full object-cover border"
          />
        ) : (
          <div className="w-14 h-14 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-lg">
            {contractor.user_first_name?.charAt(0)}
          </div>
        )}

        {/* Name + status */}
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-lg">
              {contractor.user_first_name}
            </h4>

            {type === "accepted" && (
              <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                Accepted
              </span>
            )}
          </div>

          <p className="text-xs text-gray-500">ID: {contractor.id}</p>
        </div>
      </div>

      {/* CONTACT */}
      <div className="mt-4 space-y-2 text-sm">
        <div className="flex items-center gap-2 text-gray-600">
          <Mail className="w-4 h-4" />
          <a href={`mailto:${contractor.user_email}`} className="truncate hover:text-blue-600">
            {contractor.user_email}
          </a>
        </div>

        <div className="flex items-center gap-2 text-gray-600">
          <Phone className="w-4 h-4" />
          <a href={`tel:${contractor.user_phone}`} className="hover:text-blue-600">
            {contractor.user_phone}
          </a>
        </div>
      </div>

      {/* DOCUMENTS */}
      {type === "accepted" && (
      <div className="mt-5">
        <p className="text-sm font-semibold text-gray-700 mb-2">Documents</p>

        <div className="grid grid-cols-2 gap-3">

          {/* Insurance */}
          {contractor.cc_insurance_documentation_url && (
            <div className="border rounded-lg p-2 bg-gray-50 hover:bg-gray-100 transition">
              <p className="text-xs font-medium text-gray-600 mb-1">
                Insurance
              </p>

              {contractor.cc_insurance_documentation_url.includes(".pdf") ? (
                <a
                  href={contractor.cc_insurance_documentation_url}
                  target="_blank"
                  className="text-blue-600 text-sm underline"
                >
                  View PDF
                </a>
              ) : (
                <img
                  src={contractor.cc_insurance_documentation_url}
                  className="w-full h-24 object-cover rounded"
                />
              )}

              <a  target="_blank" href={contractor.cc_insurance_documentation_url} download>
                <button className="mt-2 w-full text-xs bg-transparent text-blue-500 border-0 py-1 rounded">
                  Download
                </button>
              </a>
            </div>
          )}

          {/* License */}
          {contractor.cc_licence_documentation_url && (
            <div className="border rounded-lg p-2 bg-gray-50 hover:bg-gray-100 transition">
              <p className="text-xs font-medium text-gray-600 mb-1">
                License
              </p>

              {contractor.cc_licence_documentation_url.includes(".pdf") ? (
                <a
                  href={contractor.cc_licence_documentation_url}
                  target="_blank"
                  className="text-blue-600 text-sm underline"
                >
                  View PDF
                </a>
              ) : (
                <img
                  src={contractor.cc_licence_documentation_url}
                  className="w-full h-24 object-cover rounded"
                />
              )}

              <a  target="_blank" href={contractor.cc_licence_documentation_url} download>
              <button className="mt-2 w-full text-xs bg-transparent text-blue-500 border-0 py-1 rounded">
                  Download
                </button>
              </a>
            </div>
          )}

        </div>
      </div>
      )}
      {/* TIMESTAMP */}
      <div className="mt-4 text-xs text-gray-500 space-y-1">
        {contractor.sent_at && (
          <div>
            Sent: {new Date(contractor.sent_at).toLocaleDateString()}
          </div>
        )}
        {contractor.accepted_at && (
          <div>
            Accepted: {new Date(contractor.accepted_at).toLocaleDateString()}
          </div>
        )}
      </div>

      {/* ACTION */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onMessage(contractor);
        }}
        className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
      >
        <MessageCircle className="w-4 h-4" />
        Message
      </button>
    </div>
  );
}
